import { AnyAction } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import { filter, mapTo, Observable, switchMap, takeUntil } from 'rxjs';
import { SmoEnvironment } from '../../../../Models/Smo/SmoEnvironment';
import { mapPayload } from '../../../../Utils/RxjsOperators/MapPayload';
import { SmoSocketClientEvent } from '../../../Socket/Models/SmoSocketClientEvent';
import { SmoSocketServerEvent } from '../../../Socket/Models/SmoSocketServerEvent';
import { SmoSocketServerEventResponse } from '../../../Socket/Models/SmoSocketServerEventResponse';
import { StoreDependencies } from '../../Global/StoreDependencies';
import { StoreState } from '../../Global/StoreState';
import WidgetFrontActions from './WidgetFrontActions';

function observeWidgetConnection(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(WidgetFrontActions.observeWidgetConnection.match),
    filter(() => !!$state.value.user.currentUser),
    switchMap(() =>
      dependencies
        .socketManager!.listen$<SmoSocketServerEvent, SmoSocketServerEventResponse>(
          SmoSocketServerEvent.widget_connected
        )
        .pipe(takeUntil($action.pipe(filter(WidgetFrontActions.stopObservingWidgetConnection.match))))
    ),
    switchMap(() => [
      WidgetFrontActions.updateSettings($state.value.user.currentUser!.settings.widget),
      WidgetFrontActions.updateEnvironment($state.value.videoAnalysis.predictedEnvironment ?? SmoEnvironment.none),
      WidgetFrontActions.updateRun($state.value.user.currentUser?.run)
    ])
  );
}

function updateSettings(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(WidgetFrontActions.updateSettings.match),
    mapPayload(),
    switchMap((settings) =>
      dependencies.socketManager!.emit$(SmoSocketClientEvent.update_settings, {
        userId: $state.value.user.currentUser?.id ?? '',
        settings
      })
    ),
    mapTo(WidgetFrontActions.updateSettingsSuccess())
  );
}

function updateEnvironment(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(WidgetFrontActions.updateEnvironment.match),
    mapPayload(),
    switchMap((environment) =>
      dependencies.socketManager!.emit$(SmoSocketClientEvent.update_environment, {
        userId: $state.value.user.currentUser?.id ?? '',
        environment
      })
    ),
    mapTo(WidgetFrontActions.updateEnvironmentSuccess())
  );
}
function updateRun(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(WidgetFrontActions.updateRun.match),
    mapPayload(),
    filter((run) => !!run),
    switchMap((run) =>
      dependencies.socketManager!.emit$(SmoSocketClientEvent.update_run, {
        userId: $state.value.user.currentUser?.id ?? '',
        run
      })
    ),
    mapTo(WidgetFrontActions.updateRunSuccess())
  );
}

export default combineEpics(observeWidgetConnection, updateSettings, updateEnvironment, updateRun);
